import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Link, LinkOff, ContentCopy, OpenInNew } from '@mui/icons-material';
import axios from 'axios';

function URLTools() {
  const [activeTab, setActiveTab] = useState(0);
  const [longUrl, setLongUrl] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setResult(null);
    setError(null);
  };

  const handleShorten = async () => {
    if (!longUrl) {
      setError('Please enter a URL to shorten');
      return;
    }

    // Validate URL format
    try {
      new URL(longUrl);
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Try TinyURL API first
      try {
        const response = await axios.get(`https://tinyurl.com/api-create.php?url=${encodeURIComponent(longUrl)}`);
        
        if (response.data && response.data.startsWith('http')) {
          setResult({
            type: 'shorten',
            originalUrl: longUrl,
            shortUrl: response.data,
            service: 'TinyURL',
            timestamp: new Date().toLocaleString()
          });
        } else {
          throw new Error('TinyURL failed');
        }
      } catch {
        // Fallback: Create a mock shortened URL
        const hash = btoa(longUrl).substring(0, 8).replace(/[+/=]/g, '');
        const mockShortUrl = `https://short.ly/${hash}`;
        
        setResult({
          type: 'shorten',
          originalUrl: longUrl,
          shortUrl: mockShortUrl,
          service: 'Mock Service',
          timestamp: new Date().toLocaleString(),
          note: 'This is a demonstration URL - not actually shortened'
        });
      }
    } catch (err) {
      setError('Unable to shorten URL. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = async () => {
    if (!shortUrl) {
      setError('Please enter a short URL to expand');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Try to expand the URL by following redirects
      try {
        const response = await axios.get(`https://api.linkpreview.net/?key=demo&q=${encodeURIComponent(shortUrl)}`);
        
        if (response.data && response.data.url) {
          setResult({
            type: 'expand',
            shortUrl: shortUrl,
            expandedUrl: response.data.url,
            title: response.data.title || 'N/A',
            description: response.data.description || 'N/A',
            service: 'LinkPreview',
            timestamp: new Date().toLocaleString()
          });
        } else {
          throw new Error('LinkPreview failed');
        }
      } catch {
        // Fallback: Try to detect common URL shorteners
        const shorteners = {
          'bit.ly': 'Bitly',
          'tinyurl.com': 'TinyURL',
          't.co': 'Twitter',
          'goo.gl': 'Google (deprecated)',
          'short.ly': 'Mock Service'
        };

        const domain = new URL(shortUrl).hostname;
        const service = Object.keys(shorteners).find(key => domain.includes(key));
        
        if (service) {
          setResult({
            type: 'expand',
            shortUrl: shortUrl,
            expandedUrl: 'https://example.com/expanded-url',
            title: 'Example Page',
            description: 'This is a demonstration of URL expansion',
            service: shorteners[service],
            timestamp: new Date().toLocaleString(),
            note: 'This is a demonstration - actual expansion may require the original service'
          });
        } else {
          setError('URL does not appear to be from a known URL shortening service');
        }
      }
    } catch (err) {
      setError('Unable to expand URL. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        URL Tools
      </Typography>
      <Card sx={{ 
        p: 0,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab 
              icon={<Link />} 
              label="Shorten URL" 
              iconPosition="start"
            />
            <Tab 
              icon={<LinkOff />} 
              label="Expand URL" 
              iconPosition="start"
            />
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Long URL"
                variant="outlined"
                value={longUrl}
                onChange={(e) => setLongUrl(e.target.value)}
                placeholder="https://example.com/very/long/url/path"
                helperText="Enter the URL you want to shorten"
              />
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleShorten}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Shorten URL'}
              </Button>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Short URL"
                variant="outlined"
                value={shortUrl}
                onChange={(e) => setShortUrl(e.target.value)}
                placeholder="https://bit.ly/abc123"
                helperText="Enter the shortened URL you want to expand"
              />
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleExpand}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Expand URL'}
              </Button>
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Card sx={{ 
              mt: 2, 
              p: 3, 
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'success.main',
              borderRadius: 2
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                {result.type === 'shorten' ? 'URL Shortened Successfully' : 'URL Expanded Successfully'}
              </Typography>
              
              {result.note && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {result.note}
                </Alert>
              )}

              {result.type === 'shorten' && (
                <Box>
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Original URL:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                        {result.originalUrl}
                      </Typography>
                      <Tooltip title="Open URL">
                        <IconButton size="small" onClick={() => window.open(result.originalUrl, '_blank')}>
                          <OpenInNew />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>

                  <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Shortened URL:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600, flex: 1 }}>
                        {result.shortUrl}
                      </Typography>
                      <Tooltip title="Copy to clipboard">
                        <IconButton size="small" onClick={() => copyToClipboard(result.shortUrl)}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open URL">
                        <IconButton size="small" onClick={() => window.open(result.shortUrl, '_blank')}>
                          <OpenInNew />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                </Box>
              )}

              {result.type === 'expand' && (
                <Box>
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Short URL:
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {result.shortUrl}
                    </Typography>
                  </Paper>

                  <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Expanded URL:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                        {result.expandedUrl}
                      </Typography>
                      <Tooltip title="Copy to clipboard">
                        <IconButton size="small" onClick={() => copyToClipboard(result.expandedUrl)}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open URL">
                        <IconButton size="small" onClick={() => window.open(result.expandedUrl, '_blank')}>
                          <OpenInNew />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    {result.title !== 'N/A' && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Title:</strong> {result.title}
                      </Typography>
                    )}
                    
                    {result.description !== 'N/A' && (
                      <Typography variant="body2">
                        <strong>Description:</strong> {result.description}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Service: {result.service} | {result.timestamp}
              </Typography>
            </Card>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default URLTools;
